/**
 * External dependencies
 */
import clsx from "clsx";

/**
 * WordPress dependencies
 */
import {
  BlockIcon,
  MediaPlaceholder,
  store as blockEditorStore,
  __experimentalGetShadowClassesAndStyles as getShadowClassesAndStyles,
  useBlockEditingMode,
  useBlockProps,
  __experimentalUseBorderProps as useBorderProps,
} from "@wordpress/block-editor";
import { Button, Placeholder } from "@wordpress/components";
import { useDispatch } from "@wordpress/data";
import { useEffect, useState } from "@wordpress/element";
import { __ } from "@wordpress/i18n";
import { image as icon } from "@wordpress/icons";
import IconsModal from "../../components/IconsModal";

/**
 * Internal dependencies
 */
import Icon from "./icon";

/**
 * Module constants
 */

export function Edit({ attributes, setAttributes, className, clientId }) {
  const { align, iconName } = attributes;

  const [isIconsModalOpen, setIsIconsModalOpen] = useState(false);

  const { __unstableMarkNextChangeAsNotPersistent } =
    useDispatch(blockEditorStore);

  useEffect(() => {
    if (["wide", "full"].includes(align)) {
      __unstableMarkNextChangeAsNotPersistent();
    }
  }, [__unstableMarkNextChangeAsNotPersistent, align, setAttributes]);

  const blockEditingMode = useBlockEditingMode();

  const borderProps = useBorderProps(attributes);
  const shadowProps = getShadowClassesAndStyles(attributes);

  const classes = clsx(className, {
    "has-custom-border":
      !!borderProps.className ||
      (borderProps.style && Object.keys(borderProps.style).length > 0),
  });

  const blockProps = useBlockProps({
    className: classes,
  });

  const placeholder = (content) => {
    return (
      <Placeholder
        className="block-editor-media-placeholder"
        icon={icon}
        label={__("Icon")}
        instructions={__("Choose an icon from library.")}
      >
        <Button variant="primary" onClick={() => setIsIconsModalOpen(true)}>
          {__("Icon Library")}
        </Button>
      </Placeholder>
    );
  };

  const blockStyle = {
    ...blockProps.style,
    ...borderProps.style,
    ...shadowProps.style,
    width: "fit-content",
  };
  delete blockProps.style;

  return (
    <>
      <div
        {...blockProps}
        className={clsx(blockProps.className, borderProps.className)}
        style={{ ...blockStyle }}
      >
        <Icon
          clientId={clientId}
          attributes={attributes}
          setAttributes={setAttributes}
          setIsIconsModalOpen={setIsIconsModalOpen}
          blockEditingMode={blockEditingMode}
        />
        <MediaPlaceholder
          icon={<BlockIcon icon={icon} />}
          placeholder={placeholder}
          disableMediaButtons={iconName !== ""}
        />
      </div>

      <IconsModal
        isIconsModalOpen={isIconsModalOpen}
        setIsIconsModalOpen={setIsIconsModalOpen}
        icon={{
          name: iconName,
          type: attributes.iconType ?? "regular",
        }}
        setAttributes={(newAttrs) => {
          setAttributes({
            iconName: newAttrs.name,
            iconType: newAttrs.type,
          });
        }}
      />
    </>
  );
}

export default Edit;
